import Model from 'src/app/core/models/model';

/**
 * @model BarcodeModel
 * @description
 * Represents the data structure for barcode management within the AquaCloud application.
 * This model serves as a Data Transfer Object (DTO) for handling barcode generation,
 * storage, and retrieval operations.
 *
 * **Key Responsibilities:**
 * 1. **Range Definition**: Defines the start (`barcode_from`) and end (`barcode_to`) of a barcode sequence.
 * 2. **Tracking**: Stores metadata about who generated the barcodes (`generated_by`) and when (`generated_time`).
 * 3. **Association**: Links the barcode sequence to a specific business entity via `business_id`.
 * 4. **Identification**: Stores the unique sequence identifier (`barcode_sequence`).
 *
 * **Data Flow:**
 * - **Creation**: Populated when a user requests new barcodes to be generated.
 * - **Storage**: Sent to the backend via `BarCodeService` to persist the new barcode records.
 * - **Retrieval**: Fetched from the backend to display available or used barcodes for a business.
 *
 * **Usage:**
 * - Used by `BarCodeService` for CRUD operations.
 * - Used in components that manage inventory or asset tracking requiring barcodes.
 *
 * @extends Model
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class BarcodeModel extends Model {
  /**
   * @type {number}
   * @description The unique identifier of the user who generated this barcode batch.
   * @optional
   */
  generated_by?: number;

  /**
   * @type {Date}
   * @description The timestamp when the barcode batch was generated.
   * @optional
   */
  generated_time?: Date;

  /**
   * @type {string}
   * @description The starting value of the barcode range.
   */
  barcode_from: string;

  /**
   * @type {string}
   * @description The ending value of the barcode range.
   */
  barcode_to: string;

  /**
   * @type {string}
   * @description The total count of barcodes in this batch.
   * Note: Defined as string to handle potential large numbers or formatted counts.
   * @optional
   */
  barcode_count?: string;

  /**
   * @type {string}
   * @description The unique sequence identifier or prefix for this batch of barcodes.
   */
  barcode_sequence: string;

  /**
   * @type {string}
   * @description The unique identifier of the business associated with these barcodes.
   * @optional
   */
  business_id?: string;

  /**
   * @constructor
   * @description Initializes a new instance of the BarcodeModel.
   * Calls the parent `Model` constructor.
   */
  constructor() {
    super();
  }
}
